import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, ChevronRight } from "lucide-react";
import { useLanguage } from "../../../context/LanguageContext";
import dashboardContent from "../../../utils/dashboardContent";
import { useAuth } from "../../../context/authContext";
import useNotificationsService from "../../../services/notificationService";
import { message } from "antd";

const DropdownNotification = () => {
  const { user } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unread, setUnread] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const { getUserNotifications, markNotificationsAsRead } =
    useNotificationsService();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const { language } = useLanguage();
  const text = dashboardContent[language].dropdownNotification;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    const res = await getUserNotifications();
    setNotifications(res);
    const unread = res.filter((notification) => !notification.read);
    setUnreadNotifications(unread);
    setUnread(unread.length > 0);
  };

  const markAsRead = async (notificationId) => {
    await markNotificationsAsRead(notificationId);
    fetchNotifications();
  };

  const markAllAsRead = async () => {
    const promises = unreadNotifications.map((notification) =>
      markNotificationsAsRead(notification._id, false)
    );
    await Promise.all(promises);
    message.success("Alle Benachrichtigungen wurden als gelesen markiert.");
    fetchNotifications();
  };

  const getNotificationTime = (createdAt) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return text.justNow;
    if (diffInHours < 24) return text.hoursAgo(diffInHours);
    return date.toLocaleDateString(language === "de" ? "de-DE" : "en-US");
  };

  return (
    <div ref={dropdownRef} className="relative">
      <div
        className="relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-stroke bg-gray-200 hover:bg-gray-300 dark:border-strokedark dark:bg-meta-4"
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        {user?.notificationSettings?.appNotification && (
          <span
            className={`absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 ${
              dropdownOpen || !unread ? "hidden" : "block"
            }`}
          ></span>
        )}

        <Bell className="h-5 w-5 text-gray-600 dark:text-white" />
      </div>

      {dropdownOpen && (
        <div className="absolute right-0 mt-2 md:w-80 w-60 rounded-lg border border-stroke bg-white shadow-lg dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke px-4 py-3 dark:border-strokedark">
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-medium">{text.title}</h5>
              {unreadNotifications.length > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-500 hover:text-blue-600"
                >
                  {text.markAllRead}
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {unreadNotifications.length > 0 ? (
              <>
                <div className="bg-gray-50 px-4 py-2 dark:bg-gray-700">
                  <h6 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {text.unread(unreadNotifications.length)}
                  </h6>
                </div>
                {unreadNotifications.map((notification) => (
                  <div
                    key={notification._id}
                    className="border-b border-stroke px-4 py-3 hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 dark:text-white">
                          {notification.message}
                        </p>
                        <span className="text-xs text-gray-400">
                          {getNotificationTime(notification.createdAt)}
                        </span>
                      </div>
                      <button
                        onClick={() => markAsRead(notification._id)}
                        className="ml-3 rounded-full p-1 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <Check className="h-4 w-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="px-4 py-3">
                <p className="text-sm text-gray-500">{text.noUnread}</p>
              </div>
            )}

            {notifications.length > unreadNotifications.length && (
              <div className="border-t border-stroke px-4 py-3 dark:border-strokedark">
                <button
                  className="flex w-full items-center justify-between text-sm text-gray-500 hover:text-gray-700"
                  onClick={() => {
                    navigate("/dashboard/notifications");
                  }}
                >
                  <span>{text.viewAll}</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownNotification;
